import type React from 'react'
import { useRef, useEffect } from 'react'
import { Form, Input, Select, InputNumber, DatePicker } from 'antd'
import type { FormInstance, FormListFieldData } from 'antd'
import { GraviButton, Horizontal, Vertical, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import type { ProductOption } from '@api/useSupplierReconciliation/types/invoice'
import { useFormatter } from '@api/useFormatter'
import { useInvoiceValidation } from '../hooks/useInvoiceValidation'
import { useInvoiceCalculations } from '../hooks/useInvoiceCalculations'
import { ProductTotal } from './ProductTotal'
import { currencyFormatterWithDecimals, volumeFormatterThreeDecimals, robustParser } from '../utils/formatters'
import type { InvoiceFormValues } from '@/api/useSupplierReconciliation/types/forms'

interface InvoiceProductsSectionProps {
  form: FormInstance<InvoiceFormValues>
  fields: FormListFieldData[]
  add: () => void
  remove: (index: number) => void
  products: ProductOption[]
  dropdownOptions: {
    suppliers: Array<{ value: string; label: string }>
    terminals: Array<{ value: string; label: string }>
    products: Array<{ value: string; label: string }>
    states: Array<{ value: string; label: string }>
  }
  metadataLoading: boolean
  onCalculateTotals: () => void
  onProductRemoved?: (productId: string) => void
}

export const InvoiceProductsSection: React.FC<InvoiceProductsSectionProps> = ({
  form,
  fields,
  add,
  remove,
  products,
  dropdownOptions,
  metadataLoading,
  onCalculateTotals,
  onProductRemoved,
}) => {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  const {
    getProductRules,
    getGrossVolumeRules,
    getNetVolumeRules,
    getUnitPriceRules,
    getBOLDateTimeRules,
    getBOLNumberRules,
  } = useInvoiceValidation()

  const { calculateProductTotal } = useInvoiceCalculations()

  // Ref to track when we should focus on the newly added row
  const shouldFocusNewRow = useRef(false)
  const lastFieldsLength = useRef(fields.length)

  // Effect to focus on BOL Number input when a new product is added
  useEffect(() => {
    if (shouldFocusNewRow.current && fields.length > lastFieldsLength.current) {
      // Focus on the BOL Number input of the last added row
      const lastIndex = fields.length - 1
      const lastField = fields[lastIndex]
      if (lastField) {
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          const bolNumberInput = document.querySelector(`input[id*="products_${lastField.name}_bolNumber"]`)
          if (bolNumberInput) {
            ;(bolNumberInput as HTMLInputElement).focus()
          }
        }, 100)
      }
      shouldFocusNewRow.current = false
    }
    lastFieldsLength.current = fields.length
  }, [fields.length])

  const handleAddProduct = () => {
    shouldFocusNewRow.current = true
    add()
  }

  const handleProductChange = (index: number, field: string, value: number | string | null) => {
    calculateProductTotal(index, field as any, value, form)
    onCalculateTotals()
  }

  return (
    <Vertical gap={16} className='p-3'>
      <Vertical gap={16}>
        {fields.length > 0 && (
          <Horizontal gap={16} style={{ paddingBottom: 8, borderBottom: '1px solid var(--gray-200)' }}>
            <div style={{ flex: 1.2, margin: 0 }}>
              <Texto weight='bold'>BOL Number *</Texto>
            </div>
            <div style={{ flex: 1.3, margin: 0 }}>
              <Texto weight='bold'>BOL Date & Time *</Texto>
            </div>
            <div style={{ flex: 1.8, margin: 0 }}>
              <Texto weight='bold'>Product *</Texto>
            </div>
            <div style={{ flex: 0.8, margin: 0 }}>
              <Texto weight='bold' style={{ textAlign: 'center' }}>
                Gross Vol *
              </Texto>
            </div>
            <div style={{ flex: 0.8, margin: 0 }}>
              <Texto weight='bold' style={{ textAlign: 'center' }}>
                Net Vol *
              </Texto>
            </div>
            <div style={{ flex: 0.8, margin: 0 }}>
              <Texto weight='bold' style={{ textAlign: 'center' }}>
                Unit Price *
              </Texto>
            </div>
            <div style={{ flex: 0.8, margin: 0 }}>
              <Texto weight='bold' style={{ textAlign: 'right' }}>
                Total
              </Texto>
            </div>
            <div style={{ width: 32 }} />
          </Horizontal>
        )}
        {fields.map((field, index) => (
          <Horizontal key={field.key} gap={16} verticalCenter>
            <Form.Item
              rules={getBOLNumberRules()}
              style={{ margin: 0, flex: 1.2, minHeight: 48 }}
              name={[field.name, 'bolNumber']}
            >
              <Input
                placeholder='BOL Number'
                autoComplete='off'
                required
                onBlur={(e) => {
                  const cleanValue = e.target.value.replace(/[^a-zA-Z0-9-]/g, '')
                  if (cleanValue !== e.target.value) {
                    form.setFieldValue(['products', field.name, 'bolNumber'], cleanValue)
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              rules={getBOLDateTimeRules()}
              style={{ margin: 0, flex: 1.3, minHeight: 48 }}
              name={[field.name, 'bolDateTime']}
            >
              <DatePicker
                showTime={{ use12Hours: true, format: 'hh:mm A' }}
                placeholder='Select date & time'
                required
                style={{ width: '100%' }}
                format='MM/DD/YYYY hh:mm A'
              />
            </Form.Item>
            <Form.Item
              style={{ margin: 0, flex: 1.8, minHeight: 48 }}
              name={[field.name, 'product']}
              dependencies={['products']}
              rules={getProductRules()}
            >
              <Select
                placeholder='Select product'
                showSearch
                options={dropdownOptions.products.map((option) => {
                  const products = form.getFieldValue('products') || []
                  const isAlreadySelected = products.some(
                    (p: any, idx: number) => idx !== index && p?.product === option.value
                  )

                  return {
                    ...option,
                    disabled: isAlreadySelected,
                    label: isAlreadySelected ? `${option.label} (Already selected)` : option.label,
                  }
                })}
                loading={metadataLoading}
                filterOption={(input, option) => {
                  if (option?.disabled) return false
                  return (option?.label as string).toLowerCase().includes(input.toLowerCase())
                }}
                onChange={(value) => handleProductChange(index, 'product', value)}
              />
            </Form.Item>

            <Form.Item
              name={[field.name, 'grossVol']}
              rules={getGrossVolumeRules(form, index)}
              style={{ flex: 0.8, margin: 0, minHeight: 48 }}
            >
              <InputNumber
                placeholder='Gross Vol'
                style={{ width: '100%' }}
                min={0}
                precision={3}
                stringMode
                formatter={volumeFormatterThreeDecimals}
                parser={robustParser}
                onChange={(value: number | null) => {
                  // Check if the input currently shows special patterns but onChange received converted values
                  const inputElement = document.activeElement as HTMLInputElement
                  const inputValue = inputElement?.value

                  // If input shows ".0" but onChange got "0", preserve the ".0"
                  if (inputValue === '.0' && value === 0) {
                    handleProductChange(index, 'grossVol', '.0')
                  }
                  // If input shows "-.0" but onChange got "0", preserve the "-.0"
                  else if (inputValue === '-.0' && value === 0) {
                    handleProductChange(index, 'grossVol', '-.0')
                  } else {
                    handleProductChange(index, 'grossVol', value)
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name={[field.name, 'netVol']}
              rules={getNetVolumeRules(form, index)}
              style={{ flex: 0.8, margin: 0, minHeight: 48 }}
            >
              <InputNumber
                placeholder='Net Vol'
                style={{ width: '100%' }}
                min={0}
                precision={3}
                stringMode
                formatter={volumeFormatterThreeDecimals}
                parser={robustParser}
                onChange={(value: number | null) => {
                  // Check if the input currently shows special patterns but onChange received converted values
                  const inputElement = document.activeElement as HTMLInputElement
                  const inputValue = inputElement?.value

                  // If input shows ".0" but onChange got "0", preserve the ".0"
                  if (inputValue === '.0' && value === 0) {
                    handleProductChange(index, 'netVol', '.0')
                  }
                  // If input shows "-.0" but onChange got "0", preserve the "-.0"
                  else if (inputValue === '-.0' && value === 0) {
                    handleProductChange(index, 'netVol', '-.0')
                  } else {
                    handleProductChange(index, 'netVol', value)
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name={[field.name, 'unitPrice']}
              rules={getUnitPriceRules()}
              style={{ flex: 0.8, margin: 0, minHeight: 48 }}
            >
              <InputNumber
                placeholder='Unit Price'
                style={{ width: '100%' }}
                min={0}
                precision={6}
                prefix={currencySymbol}
                stringMode
                formatter={currencyFormatterWithDecimals}
                parser={robustParser}
                onChange={(value: number | null) => {
                  // Check if the input currently shows special patterns but onChange received converted values
                  const inputElement = document.activeElement as HTMLInputElement
                  const inputValue = inputElement?.value

                  // If input shows ".0" but onChange got "0", preserve the ".0"
                  if (inputValue === '.0' && value === 0) {
                    handleProductChange(index, 'unitPrice', '.0')
                  }
                  // If input shows "-.0" but onChange got "0", preserve the "-.0"
                  else if (inputValue === '-.0' && value === 0) {
                    handleProductChange(index, 'unitPrice', '-.0')
                  } else {
                    handleProductChange(index, 'unitPrice', value)
                  }
                }}
              />
            </Form.Item>

            <Form.Item name={[field.name, 'total']} style={{ flex: 0.8, margin: 0, minHeight: 48 }}>
              <ProductTotal value={form.getFieldValue(['products', index, 'total']) || 0} />
            </Form.Item>

            <div style={{ width: 32 }}>
              <GraviButton
                appearance='outlined'
                error
                onClick={() => {
                  const products = form.getFieldValue('products') || []
                  const productToRemove = products[index]
                  const productId = productToRemove?.product

                  if (fields.length > 1) {
                    remove(field.name)
                    if (productId && onProductRemoved) {
                      onProductRemoved(productId)
                    }
                  } else {
                    const clearedProducts = [...products]
                    clearedProducts[index] = {
                      bolNumber: undefined,
                      bolDateTime: undefined,
                      product: undefined,
                      grossVol: undefined,
                      netVol: undefined,
                      unitPrice: undefined,
                      total: undefined,
                    }
                    form.setFieldsValue({ products: clearedProducts })
                    onCalculateTotals()
                    if (productId && onProductRemoved) {
                      onProductRemoved(productId)
                    }
                  }
                }}
                icon={<MaterialIcon icon='delete' />}
              />
            </div>
          </Horizontal>
        ))}
        <GraviButton appearance='outlined' theme2 onClick={handleAddProduct} block icon={<MaterialIcon icon='add' />}>
          Add Product Line Item
        </GraviButton>
      </Vertical>
    </Vertical>
  )
}
